/**
 * 门店信息概览：由 PMS 业务报告单日行 + 实时房态房型行拼出展示结构。
 * 说明：日度报表通常为「上一营业日」；在住/客源等为间夜口径，与 PMS 报表字段一致。
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
function n(v) {
    var x = typeof v === "number" ? v : Number(v);
    return Number.isFinite(x) ? x : 0;
}
function formatReportDateLabel(dateSec) {
    if (!dateSec)
        return "—";
    var d = new Date(dateSec * 1000);
    return "".concat(d.getMonth() + 1, "/").concat(d.getDate());
}
/**
 * @param reportRow PMS businessReportList 中最新一条（按 date 降序取）
 * @param roomRows 房态 dataSource（含全店行与各房型）
 */
export function buildStoreOverviewSnapshot(reportRow, roomRows) {
    var row = reportRow !== null && reportRow !== void 0 ? reportRow : {};
    var roomTotalFromRows = Array.isArray(roomRows)
        ? roomRows
            .filter(function (r) { return !r.isStoreTotal; })
            .reduce(function (s, r) { return s + n(r.quantity); }, 0)
        : 0;
    var roomTotal = n(row.roomTotal) || n(row.roomNum) || roomTotalFromRows;
    var sellableByRoomType = Array.isArray(roomRows)
        ? roomRows
            .filter(function (r) { return !r.isStoreTotal && r.label; })
            .map(function (r) { return ({
            label: String(r.label),
            qty: Math.round(n(r.quantityOpen)),
        }); })
            .filter(function (x) { return x.qty > 0; })
        : [];
    var sellableFromRows = sellableByRoomType.reduce(function (s, x) { return s + x.qty; }, 0);
    var storeTotalRowForSell = Array.isArray(roomRows)
        ? roomRows.find(function (r) { return r.isStoreTotal; })
        : undefined;
    var sellableFromStoreAgg = storeTotalRowForSell
        ? Math.round(n(storeTotalRowForSell.quantityOpen))
        : 0;
    /** 可售间数仅用房态（FSM）数据源：各房型 quantityOpen 之和；明细全为 0 时用全店汇总行 quantityOpen */
    var sellableTotal = 0;
    if (sellableFromRows > 0) {
        sellableTotal = Math.round(sellableFromRows);
    }
    else if (sellableFromStoreAgg > 0) {
        sellableTotal = sellableFromStoreAgg;
    }
    var dateSec = n(row.date);
    var adrVal = Math.round(n(row.adr) * 100) / 100;
    var roomTakeN = Math.round(n(row.roomTake));
    var orderRoomTakeN = Math.round(n(row.orderRoomTake));
    var predictedRevenue = Math.round((roomTakeN * adrVal + orderRoomTakeN * adrVal) * 100) / 100;
    return {
        reportDateLabel: formatReportDateLabel(dateSec),
        roomTotal: Math.round(roomTotal),
        roomRepair: Math.round(n(row.roomRepair)),
        roomLock: Math.round(n(row.roomLock)),
        roomTake: roomTakeN,
        stayingChannels: [
            { label: "中介", value: Math.round(n(row.agencyTotalRoomNights)) },
            { label: "OTA", value: Math.round(n(row.otaRoomNight)) },
            { label: "协议", value: Math.round(n(row.unitRoomNight)) },
            { label: "会员", value: Math.round(n(row.memberRoomNight)) },
            { label: "散客", value: Math.round(n(row.fitRoomNight)) },
        ],
        predDepart: Math.round(n(row.orderRoomCheckout)),
        earlyCheckout: Math.round(n(row.earlyCheckout)),
        orderRoomTake: orderRoomTakeN,
        paymentGuarantee: row.guaranteeRoomNight != null
            ? Math.round(n(row.guaranteeRoomNight))
            : null,
        paymentPrepay: row.prepayRoomNight != null ? Math.round(n(row.prepayRoomNight)) : null,
        paymentCash: row.cashPayRoomNight != null ? Math.round(n(row.cashPayRoomNight)) : null,
        sellableTotal: Math.round(sellableTotal),
        sellableByRoomType: sellableByRoomType,
        adr: adrVal,
        roomRevenue: Math.round(n(row.roomRevenue) * 100) / 100,
        adrWeekCompare: null,
        adrYearAvg: null,
        predictedRevenue: predictedRevenue,
        orderTrackingNote: null,
    };
}
/**
 * 用日度 report 列表补充周同比/年均价（ADR 的对比与均值）
 */
export function enrichStoreOverviewFromReportList(list, base) {
    var _a;
    if (!list || list.length === 0) {
        return base;
    }
    var sorted = __spreadArray([], list, true).sort(function (a, b) { return (b.date || 0) - (a.date || 0); });
    var latest = sorted[0];
    var latestDate = (_a = latest === null || latest === void 0 ? void 0 : latest.date) !== null && _a !== void 0 ? _a : 0;
    if (!latestDate)
        return base;
    var targetWeek = latestDate - 7 * 86400;
    var weekRow;
    var minDiff = Infinity;
    for (var _i = 0, sorted_1 = sorted; _i < sorted_1.length; _i++) {
        var r = sorted_1[_i];
        var d = Math.abs((r.date || 0) - targetWeek);
        if (d < minDiff) {
            minDiff = d;
            weekRow = r;
        }
    }
    var adrWeekRaw = weekRow && minDiff < 3.5 * 86400 ? n(weekRow.adr) : null;
    var adrWeekCompare = adrWeekRaw != null && adrWeekRaw > 0
        ? Math.round(adrWeekRaw * 100) / 100
        : null;
    var adrVals = sorted.map(function (r) { return n(r.adr); }).filter(function (x) { return x > 0; });
    var adrYearAvg = adrVals.length > 0
        ? Math.round((adrVals.reduce(function (a, b) { return a + b; }, 0) / adrVals.length) * 100) / 100
        : null;
    return __assign(__assign({}, base), { adrWeekCompare: adrWeekCompare, adrYearAvg: adrYearAvg });
}
/**
 * 与下方房单卡同源：FSM 覆盖报表在住/预抵/可售/预离等，并重算预测收入与库存平衡。
 * - in-house-today → roomTake
 * - expected-arrival-today → orderRoomTake
 * - 房态各房型 quantityOpen 合计 → sellableTotal（可售，仅 FSM，不用报表 roomAvaliable）；有 FSM 在住时用总房 − 在住 − 停售约束可售上限
 * - expected-departure-today：代理 summary（房单全量）未退房(≠5) → predDepart，已退房(5) → earlyCheckout；无报表兜底
 * - 维修：报表常为间夜口径，用「总房 − 在住 − 可售 − 停售」剩余间数上限约束
 */
export function mergeSnapshotWithFsmInHouseCount(base, fsmInHouseCount, fsmExpectedArrivalCount, fsmSellableQuantityOpen, fsmPredDepartCount, fsmEarlyCheckoutCount) {
    var hasIn = fsmInHouseCount != null && Number.isFinite(fsmInHouseCount);
    var hasPre = fsmExpectedArrivalCount != null && Number.isFinite(fsmExpectedArrivalCount);
    var hasSell = fsmSellableQuantityOpen != null && Number.isFinite(fsmSellableQuantityOpen);
    var hasPred = fsmPredDepartCount != null && Number.isFinite(fsmPredDepartCount);
    var hasEarly = fsmEarlyCheckoutCount != null && Number.isFinite(fsmEarlyCheckoutCount);
    if (!hasIn && !hasPre && !hasSell && !hasPred && !hasEarly) {
        return base;
    }
    var rt = hasIn
        ? Math.max(0, Math.round(fsmInHouseCount))
        : base.roomTake;
    var ort = hasPre
        ? Math.max(0, Math.round(fsmExpectedArrivalCount))
        : base.orderRoomTake;
    var adr = base.adr;
    var predictedRevenue = hasIn || hasPre
        ? Math.round((rt * adr + ort * adr) * 100) / 100
        : base.predictedRevenue;
    var lockN = Math.max(0, Math.min(base.roomLock, base.roomTotal));
    var sell = hasSell
        ? Math.max(0, Math.round(fsmSellableQuantityOpen))
        : base.sellableTotal;
    /** 可售仅信 FSM；有 FSM 在住时用「总房 − 在住 − 停售」做物理上限（与房态同源），无 FSM 在住时不做报表口径压限 */
    if (hasSell) {
        if (hasIn) {
            sell = Math.min(sell, Math.max(0, base.roomTotal - rt - lockN));
        }
    }
    else {
        sell = Math.max(0, Math.min(sell, Math.max(0, base.roomTotal - rt - lockN)));
    }
    var repairCap = Math.max(0, base.roomTotal - rt - sell - lockN);
    var repairReport = Math.max(0, Math.round(base.roomRepair));
    var roomRepair = Math.min(repairReport, repairCap);
    var predDepart = hasPred
        ? Math.max(0, Math.round(fsmPredDepartCount))
        : base.predDepart;
    var earlyCheckout = hasEarly
        ? Math.max(0, Math.round(fsmEarlyCheckoutCount))
        : base.earlyCheckout;
    return __assign(__assign({}, base), { roomTake: rt, orderRoomTake: ort, sellableTotal: sell, roomRepair: roomRepair, predDepart: predDepart, earlyCheckout: earlyCheckout, predictedRevenue: predictedRevenue });
}
